import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { useAppSelector } from '../../app/store';
import { useCan } from '../../core/authorization/PermissionProvider';
import { feedApi } from '../../core/api/client';
import { PHOTO_TYPES, uploadPhoto } from '../hub/ProfilePhotos';

const MAX_CONTENT = 4000;
/** PlatformAPI PublicPosts.MaxPhotos. */
const MAX_PHOTOS = 5;

interface PendingPhoto { file: File; preview: string }

/**
 * "What's new?" box at the top of the Hub feeds (landing, `/social`). Publishes a Public post as the
 * visitor's current organization through the workspace feed API, so it needs a session with
 * `platform.feed.create`; everyone else sees nothing. `onPublished` reloads the list.
 */
export function PostComposer({ onPublished }: { onPublished: () => void }) {
  const { t } = useTranslation();
  const signedIn = useAppSelector((state) => state.session.status) === 'authenticated';
  const canCreate = useCan('platform.feed', 'create');
  const profile = useAppSelector((state) => state.session.profile);
  const organization = useAppSelector((state) => state.session.organization);
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const photosRef = useRef<PendingPhoto[]>([]);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  // Grow with the text instead of scrolling inside a small box.
  useEffect(() => {
    const el = text.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [content, open]);

  useEffect(() => {
    if (!notice || notice.error) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!signedIn || !canCreate) return null;

  const author = organization.name || `${profile.firstName} ${profile.lastName}`.trim();
  const initial = (author || '?').slice(0, 1).toUpperCase();

  const addPhotos = (files: FileList | null) => {
    const chosen = Array.from(files ?? []);
    const valid = chosen.filter((file) => PHOTO_TYPES.includes(file.type) && file.size > 0);
    const room = MAX_PHOTOS - photos.length;
    if (valid.length < chosen.length) setNotice({ text: t('hub.posts.photoTypes', 'Choose JPEG, PNG, WebP, GIF or AVIF photos.'), error: true });
    else if (valid.length > room) setNotice({ text: t('hub.posts.maxPhotos', 'A post can have at most {{count}} photos.', { count: MAX_PHOTOS }), error: true });
    setPhotos((current) => [...current, ...valid.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    setOpen(true);
  };

  const removePhoto = (index: number) => setPhotos((current) => {
    URL.revokeObjectURL(current[index].preview);
    return current.filter((_, position) => position !== index);
  });

  const reset = () => {
    photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
    setPhotos([]);
    setContent('');
    setOpen(false);
  };

  async function publish(event?: FormEvent) {
    event?.preventDefault();
    const trimmed = content.trim();
    if ((!trimmed && photos.length === 0) || posting) return;
    setPosting(true);
    setNotice(null);
    try {
      const mediaIds: string[] = [];
      for (const photo of photos) mediaIds.push(await uploadPhoto('/feed/posts/media/upload-session', photo.file));
      await feedApi.createPost(trimmed, 'Public', mediaIds);
      reset();
      setNotice({ text: t('hub.posts.published', 'Post published.'), error: false });
      onPublished();
    } catch (error) {
      setNotice({ text: error instanceof Error && error.message ? error.message : t('hub.posts.publishFailed', 'Your post could not be published.'), error: true });
    } finally { setPosting(false); }
  }

  const ready = (content.trim().length > 0 || photos.length > 0) && !posting;

  return <form className={`hub-composer${open ? ' open' : ''}`} onSubmit={publish}>
    <div className="hub-composer-row">
      <span className="hub-composer-avatar" aria-hidden>{initial}</span>
      <label className="hub-sr" htmlFor="hub-composer-text">{t('hub.posts.composerPlaceholder', 'What is new at your business?')}</label>
      <textarea id="hub-composer-text" ref={text} rows={1} value={content} maxLength={MAX_CONTENT} disabled={posting}
        placeholder={t('hub.posts.composerPlaceholder', 'What is new at your business?')}
        onFocus={() => setOpen(true)} onChange={(event) => setContent(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); void publish(); } }} />
    </div>

    {photos.length > 0 && <ul className="hub-composer-photos">
      {photos.map((photo, index) => <li key={photo.preview}>
        <img src={photo.preview} alt="" />
        <button type="button" onClick={() => removePhoto(index)} disabled={posting} aria-label={t('hub.posts.removePhoto', 'Remove photo')}>
          <CloseRoundedIcon aria-hidden />
        </button>
      </li>)}
    </ul>}

    <div className="hub-composer-bar">
      <label className={`hub-composer-tool${posting || photos.length >= MAX_PHOTOS ? ' disabled' : ''}`}>
        <AddPhotoAlternateRoundedIcon aria-hidden /><span>{t('hub.posts.addPhotos', 'Photo')}</span>
        <input type="file" multiple accept={PHOTO_TYPES.join(',')} disabled={posting || photos.length >= MAX_PHOTOS} className="hub-sr"
          onChange={(event) => { addPhotos(event.target.files); event.target.value = ''; }} />
      </label>
      {open && <span className="hub-composer-as"><PublicRoundedIcon aria-hidden />{t('hub.posts.postingAs', 'Posting publicly as {{name}}', { name: author })}</span>}
      {open && content.length > MAX_CONTENT * 0.9 && <span className="hub-composer-count">{content.length}/{MAX_CONTENT}</span>}
      <div className="hub-composer-actions">
        {open && <button type="button" className="hub-btn sm ghost" onClick={reset} disabled={posting}>{t('hub.posts.cancel', 'Cancel')}</button>}
        <button type="submit" className="hub-btn sm" disabled={!ready}>
          {posting ? t('hub.posts.publishing', 'Publishing…') : t('hub.posts.publish', 'Publish')}
        </button>
      </div>
    </div>
    {notice && <p className={`hub-composer-notice${notice.error ? ' error' : ''}`} role={notice.error ? 'alert' : 'status'}>{notice.text}</p>}
  </form>;
}
